import React, { useState, useEffect } from "react";
import { Paper, Typography, Box, Button, Stack, AppBar, Toolbar, IconButton } from "@mui/material";
import { ArrowBack as ArrowBackIcon, Add } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import BulletinBoardForm from "../components/BulletinBoardAdmin/BulletinBoardForm";
import BulletinBoardList from "../components/BulletinBoardAdmin/BulletinBoardList";
import { useUserContext } from "../context/UserContext";
import { GetBulletins, CreateBulletin, UpdateBulletin, DeleteBulletin, BulletinItem, CreateBulletinPayload, UpdateBulletinPayload } from "../utils/BulletinBoardAPIRequest";

const BulletinBoardAdmin: React.FC = () => {
    const [items, setItems] = useState<BulletinItem[]>([]);
    const [editingItem, setEditingItem] = useState<BulletinItem | null>(null);
    const [showForm, setShowForm] = useState(false);
    const theme = useTheme();
    const { isAdmin, isAuthenticated, handleLogout } = useUserContext();
    const navigate = useNavigate();

    // Fetch bulletins from API
    useEffect(() => {
        const fetchBulletins = async () => {
            try {
                const data = await GetBulletins();
                setItems(data);
            } catch (e) {
                setItems([]);
            }
        };
        fetchBulletins();
    }, []);

    const handleCreate = () => {
        setEditingItem(null);
        setShowForm(true);
    };

    const handleEdit = (item: BulletinItem) => {
        setEditingItem(item);
        setShowForm(true);
    };

    const handleDelete = async (_id: string) => {
        try {
            await DeleteBulletin(_id);
            setItems((prev) => prev.filter((item) => item._id !== _id));
        } catch (e) {
            // Optionally show error
        }
    };

    const handleFormSubmit = async (item: BulletinItem) => {
        console.log("Form submitted with item:", item);
        if (editingItem) {
            try {
                const updated = await UpdateBulletin(item._id, { title: item.title, content: item.content });
                setItems((prev) => prev.map((i) => (i._id === item._id ? updated : i)));
            } catch (e) {
                // Optionally show error
            }
        } else {
            try {
                // Remove _id and createdAt for new item creation
                const { _id, createdAt, ...rest } = item;
                const created = await CreateBulletin({ title: item.title, content: item.content });

                setItems((prev) => [...prev, created]);
            } catch (e) {
                // Optionally show error
            }
        }
        setShowForm(false);
        setEditingItem(null);
    };

    const handleFormCancel = () => {
        setShowForm(false);
        setEditingItem(null);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", width: "100%", bgcolor: theme.palette.background.default }}>
            <AppBar position="fixed" elevation={0} sx={{ background: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.primary.main }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={() => navigate("/")}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: theme.palette.mode === 'dark' ? theme.palette.text.primary : undefined }}>
                        Bulletin Board Management
                    </Typography>
                </Toolbar>
            </AppBar>
            <Box sx={{ pt: 4, pb: 10, px: 3, minHeight: "100vh", background: theme.palette.background.default }}>
                <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, background: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.paper, color: theme.palette.text.primary }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>Manage Bulletins</Typography>
                        {!showForm &&
                            <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
                                New Item
                            </Button>}
                    </Box>
                    {showForm ? (
                        <BulletinBoardForm
                            item={editingItem}
                            onSubmitItem={handleFormSubmit}
                            onCancel={handleFormCancel}
                        />
                    ) : (
                        <BulletinBoardList
                            items={items}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default BulletinBoardAdmin;
